<template>
  <div
    v-if='editMode'
    v-bind='$attrs'
  >
    <div class='relative flex items-start'>
      <div class='flex h-5 items-center'>
        <input
          :id='uniqueName'
          :checked='modelValue'
          :disabled='disabled'
          :name='name + (field.id && `-${field.id}`)'
          type='checkbox'
          class='h-4 w-4 cursor-pointer rounded border-gray-300 text-green-600 focus-within:ring-0 focus:ring-2 focus:ring-gray-500'
          data-cy='checkbox-button'
          @input.prevent='toggleValue'
          @keydown.enter='toggleValue'
        >
      </div>
      <div class='ml-2 text-sm'>
        <div class='flex items-center gap-x-0.5'>
          <QuestionMarkCircleIcon
            v-if='help'
            class='question-mark-circle-icon mb-1 mr-0.5 inline-block h-4 w-4 cursor-pointer'
            @click.stop='setHelpModalVisibility(true)'
          />
          <label
            :for='uniqueName'
            class='font-medium'
            :class='[(disabled && editMode) ? "text-gray-400" : "text-gray-700 cursor-pointer"]'
            data-cy='label'
            :title='label'
            @click='labelClick'
            v-html='getLabel()'
          />
        </div>
        <p
          v-if='description'
          :class='[(disabled && editMode) ? "text-gray-300" : "text-gray-500 cursor-pointer select-none"]'
          @click='toggleValue'
        >
          {{ description }}
        </p>
      </div>
    </div>
    <InputError :error='error'/>
  </div>
  <div
    v-else
    v-bind='$attrs'
  >
    <StaticListItem
      :label='getLabel()'
      :description='description'
      :checked='modelValue'
    />
  </div>
  <Modal
    data-cy='help-modal'
    :open='helpVisible'
    :title='label'
    @close-modal='setHelpModalVisibility(false)'
  >
    <template #content>
      <p v-html='help'/>
    </template>
  </Modal>
</template>

<script lang="ts">
  import { computed, defineComponent } from 'vue'
  import useInputFieldBase, { InputFieldProps, InputFieldComponents, InputFieldEmits } from '../../../compositions/useInputFieldBase'
  import StaticListItem from '../base/StaticListItem.vue'
  import useInputFieldHasHelp, { InputFieldHasHelpProps, InputFieldHasHelpComponents } from '../../../compositions/useInputFieldHasHelp'

  export default defineComponent({
    name: 'CheckboxField',
    components: {
      ...InputFieldComponents,
      ...InputFieldHasHelpComponents,
      StaticListItem,
    },
    props: {
      ...InputFieldProps,
      ...InputFieldHasHelpProps,
      description: {
        type: String,
        default: null,
      },
    },
    emits: [
      ...InputFieldEmits,
      'checkbox-clicked',
    ],
    setup(props) {
      const { updateValue } = useInputFieldBase()
      const { helpVisible, setHelpModalVisibility } = useInputFieldHasHelp()

      const getLabel = () => {
        if (props.field.mandatory) {
          return '<span class="text-red-600" data-cy="mandatory-field">* </span>' + props?.label ?? ''
        } else {
          return props?.label ?? ''
        }
      }

      const uniqueName = computed(() => {
        return props.name + (props.field.id ? `-${props.field.id}` : '')
      })

      function toggleValue() {
        if(!props.disabled) {
          updateValue(!props.modelValue)
        }
      }

      function labelClick(e: Event) {
        if(props.disabled) {
          e.preventDefault()
          e.stopPropagation()
        }
      }

      return {
        updateValue, toggleValue, labelClick, getLabel, uniqueName, helpVisible, setHelpModalVisibility,
      }
    },
  })
</script>
